/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { processAfterSubmitAction, setRecipients, setPristine } from '#action/shipment/form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { useStepSubmit } from '#component/shipment/form/use-step-submit';
import Form from './form';
import validate from './form/validate';
import { getInitialRecipients, getSavedInitialRecipients, getTransformedRecipients } from './helper';

const handleFormSubmit = dispatch => async values => {
  const recipients = getTransformedRecipients(values.recipients);
  dispatch(setRecipients(recipients, values.recipients));
  await dispatch(processAfterSubmitAction());
};

const NotificationForm = props => {
  const { initialValues, onSubmit, editable, registerStepSubmit, form } = props;
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver((values, context) => validate(values, { ...props, ...context })),
    defaultValues: initialValues || { recipients: [] },
    mode: 'onChange'
  });

  const { reset, formState: { isDirty } } = methods;
  const prevInitialRef = React.useRef(null);

  useEffect(() => {
    if (!initialValues) return;
    const serialized = JSON.stringify(initialValues);
    if (prevInitialRef.current === serialized) return;
    prevInitialRef.current = serialized;
    if (!isDirty) {
      reset(initialValues);
    }
  }, [initialValues, reset, isDirty]);

  useEffect(() => {
    if (form) {
      dispatch(setPristine(form, !isDirty));
    }
  }, [dispatch, form, isDirty]);

  const onFormSubmit = async values => {
    if (editable) {
      await onSubmit(values);
    } else {
      dispatch(processAfterSubmitAction());
    }
  };

  useStepSubmit(registerStepSubmit, methods, onFormSubmit);

  return (
    <FormProvider {...methods}>
      <Form {...props} onFormSubmit={onFormSubmit} />
    </FormProvider>
  );
};

const mapStateToProps = (state, props) => {
  const { shipment, usersByProject, shipmentForm } = state;
  const { dispatch, editable } = props;

  const getRecipients = () => {
    // values already entered in this step win, otherwise saved recipients for an existing shipment
    if (shipmentForm.recipientFormItems?.length) {
      return shipmentForm.recipientFormItems;
    }
    if (shipment.id) {
      return getSavedInitialRecipients(shipment.recipients ?? []);
    }
    return getInitialRecipients(usersByProject.items);
  };

  const initialValues = { recipients: getRecipients() };

  return {
    initialValues,
    shipment,
    editable,
    onSubmit: handleFormSubmit(dispatch)
  };
};

export default connect(mapStateToProps)(NotificationForm);
